/**
 * Atividades comerciais (spec 02 §4.8 · spec 03 §3 · telas 3.2, 3.4 e 3.6 · passo 4.2c).
 *
 * ## O que NÃO existe aqui
 *
 * **Próxima ação não é campo da atividade.** Ela é do NEGÓCIO (`proximaAcao`), e é de
 * lá que a higiene do pipeline a lê. Duplicá-la daria duas respostas para "qual é a
 * próxima ação deste negócio?", e a tela escolheria uma sem critério.
 *
 * A atividade guarda o que ACONTECEU; o negócio guarda o que FALTA.
 */
import { z } from 'zod'
import { uuid } from './comum.js'

/**
 * Contato do cadastro OU nome livre — exatamente um (D-46).
 *
 * `nome` vem resolvido para que a tela não precise saber de qual dos dois veio; os
 * dois campos crus continuam expostos porque o chip do cadastro e o de nome livre têm
 * aparências diferentes, e essa diferença é informação.
 */
export const participanteDaAtividade = z.object({
  contatoId: uuid.nullable(),
  nomeLivre: z.string().nullable(),
  nome: z.string(),
})
export type ParticipanteDaAtividade = z.infer<typeof participanteDaAtividade>

export const atividade = z.object({
  id: uuid,
  tipoAtividadeId: uuid,
  tipoNome: z.string(),
  /** DS-171: horário legado de São Paulo sem offset, preservado para ordenação. */
  quando: z.string(),
  /** AB056: instante UTC para a apresentação no fuso escolhido. */
  quandoInstante: z.iso.datetime().optional(),
  resumo: z.string(),
  negocioId: uuid.nullable(),
  empresaId: uuid.nullable(),
  contatoId: uuid.nullable(),
  registradoPorNome: z.string().nullable(),
  participantes: z.array(participanteDaAtividade),
})
export type Atividade = z.infer<typeof atividade>

/**
 * Uma linha da linha do tempo.
 *
 * A 3.6 mescla atividades com as transições de estágio que já vêm em
 * `GET /negocios/:id` (DS-17). Esta é a forma COMUM das duas — e ela existe no
 * contrato, e não só na tela, porque a 3.2 e a 3.4 desenham a mesma lista a partir de
 * atividades puras. Um formato só, três fichas.
 *
 * `tipo` é campo, não heurística sobre o texto: "Negócio ganho" é verde porque a
 * origem disse `ganho`, não porque a frase tem a palavra.
 */
export const linhaDaTimeline = z.object({
  id: z.string(),
  tipo: z.enum(['atividade', 'estagio', 'ganho', 'perdido']),
  quando: z.string(),
  quandoInstante: z.iso.datetime().optional(),
  /** "Reunião" · "Proposta → Negociação" · "Negócio ganho". */
  titulo: z.string(),
  detalhe: z.string().nullable(),
  autorNome: z.string().nullable(),
  /**
   * Os participantes por extenso, com o contato do cadastro quando houver (D-136, DS-538):
   * o nome abre a ficha do contato em nova aba. O nome livre tem o id nulo e fica texto. A
   * API já entregava o `contatoId` na atividade; era esta linha que o descartava.
   */
  participantes: z.array(z.object({ contatoId: uuid.nullable(), nome: z.string() })),
})
export type LinhaDaTimeline = z.infer<typeof linhaDaTimeline>

export const entradaDeAtividade = z.object({
  tipoAtividadeId: uuid,
  /** ISO ou `YYYY-MM-DDTHH:mm`. Ausente = agora, resolvido no servidor. */
  quando: z.string().nullish(),
  resumo: z.string().trim().min(1, 'Descreva o que aconteceu.'),
  /**
   * Sem campo em tela desde a D-152. Aceito só para a aba aberta antes da publicação: a API
   * anexa o texto ao fim da descrição (DS-565), em vez de o Zod descartá-lo calado. Sai com a
   * coluna (BACKLOG 4.55).
   */
  resultado: z.string().nullish(),
  negocioId: uuid.nullish(),
  empresaId: uuid.nullish(),
  contatoId: uuid.nullish(),
  participantes: z
    .array(z.object({ contatoId: uuid.nullish(), nomeLivre: z.string().nullish() }))
    .default([]),
})
export type EntradaDeAtividade = z.input<typeof entradaDeAtividade>

/**
 * O corpo de `POST /negocios/:id/concluir-proxima-acao`.
 *
 * Concluir é UM caso de uso com dois efeitos — zerar a pendência do negócio e
 * registrar o que aconteceu —, e por isso é uma transação só (DS-163). O corpo é o da
 * atividade sem as âncoras: o negócio já está na rota.
 */
export const entradaDeConclusao = entradaDeAtividade.omit({
  negocioId: true,
  empresaId: true,
  contatoId: true,
})
export type EntradaDeConclusao = z.input<typeof entradaDeConclusao>
